import { Fragment } from 'react';
import classNames from 'classnames';

import { isValidUrl, normalizeUrl } from '@/public/utils/url';

import { UrlChip } from './url-chip';

import styles from './message-with-url-chips.module.scss';

interface MessageWithUrlChipsProps {
  message: string;
  className?: string;
}

const WITHOUT_PROTOCOL =
  /[^\s!#$%^&*()+={}[\]|\\:;"'<>,.?]+(\.[^\s!#^*(){}[\]|\\:;"'<>,.]+)+(#[^\s]*)?/;

const WITH_PROTOCOL = new RegExp(`https?://${WITHOUT_PROTOCOL.source}`);

// Prioritize non-URL numbers like version numbers over IP addresses
const NUMERIC = /^[0-9.#]+$/;

// Ignore emails
const EMAIL = /^[^#?]*@.+$/;

const URL_REGEX = new RegExp(`(${WITH_PROTOCOL.source})|(${WITHOUT_PROTOCOL.source})`, 'g');

const VALID_TLDS = new Set([
  'com',
  'org',
  'net',
  'edu',
  'gov',
  'io',
  'ai',
  'dev',
  'app',
  'tech',
  'cloud',
  'co',
  'me',
  'so',
  'xyz',
  'us',
  'eu',
  'de',
  'fr',
  'mx',
]);

const hasValidTLD = (url: string): boolean => {
  // Extract TLD - get everything after the last dot, before any path/query/hash
  const match = url.match(/\.([a-z]+)(?:[/#?]|$)/i);
  if (!match) {
    return false;
  }

  const tld = match[1].toLowerCase();
  return VALID_TLDS.has(tld);
};

const hasProtocol = (url: string): boolean => {
  return /^https?:\/\//i.test(url);
};

export const extractUrlsFromText = (text: string): string[] => {
  const matches = text.match(URL_REGEX);
  return (matches || []).filter((url) => {
    // Basic exclusions
    if (NUMERIC.test(url) || EMAIL.test(url)) {
      return false;
    }

    if (hasProtocol(url)) {
      return isValidUrl(url);
    }

    // Without protocol, require valid TLD to avoid false positives like "e.g" or "in.their"
    return hasValidTLD(url) && isValidUrl(normalizeUrl(url));
  });
};

const replaceUrlsWithPlaceholders = (text: string): { text: string; urls: string[] } => {
  const urls = extractUrlsFromText(text);
  let processedText = text;

  urls.forEach((url, index) => {
    processedText = processedText.replace(url, `__URL_PLACEHOLDER_${index}__`);
  });

  return { text: processedText, urls };
};

export const MessageWithUrlChips = ({ message, className }: MessageWithUrlChipsProps) => {
  const { text, urls } = replaceUrlsWithPlaceholders(message);

  const parts = text.split(/(__URL_PLACEHOLDER_\d+__)/);

  return (
    <div className={classNames(styles.messageContainer, className)}>
      {parts.map((part, index) => {
        const placeholderMatch = part.match(/^__URL_PLACEHOLDER_(\d+)__$/);
        if (placeholderMatch) {
          const urlIndex = parseInt(placeholderMatch[1], 10);
          const url = urls[urlIndex];
          return url ? <UrlChip key={index} url={url} className={styles.urlChip} /> : null;
        }
        return part !== '' ? <Fragment key={index}>{part}</Fragment> : null;
      })}
    </div>
  );
};
